import { useEffect, useRef, useState } from "react";
import { X, Zap, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";

// Ascending order so the tightest threshold >= daysLeft wins.
// e.g. daysLeft=7 → picks 7 (not 30/14). daysLeft=2 → picks 3.
const TRIGGER_DAYS = [1, 3, 7, 14, 30];
const LS_PREFIX = "pfo_trial_end_shown_v1_";

/**
 * TrialEndingModal — well-timed nudge to convert trial → paid.
 * Shows on dashboard mount when the user is on a trial AND within a
 * trigger threshold of expiry. One CTA to the pricing page; skip
 * option stays honest (users can always self-upgrade later).
 *
 * Doesn't render for admins or Pro users. Non-blocking — dismissable
 * with backdrop tap or X.
 */
export default function TrialEndingModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, trialStatus } = useAuth();
  const [open, setOpen] = useState(false);
  const [threshold, setThreshold] = useState(null);
  // Guard against React 18 StrictMode double-invoke of effects, which
  // would otherwise burn two localStorage flags per mount.
  const didEvaluate = useRef(false);

  useEffect(() => {
    if (!user || user.is_admin) return;
    if (!trialStatus || trialStatus.is_pro) return;
    if (didEvaluate.current) return;
    const daysLeft = Number(trialStatus.days_left);
    if (!Number.isFinite(daysLeft) || daysLeft < 0) return;
    didEvaluate.current = true;

    // Iterate ascending — the FIRST match is the tightest threshold
    // that still contains daysLeft (e.g. 7d left → shows the "7-day"
    // prompt, not the older "30-day" one).
    for (const days of TRIGGER_DAYS) {
      if (daysLeft <= days) {
        const key = LS_PREFIX + days;
        try {
          if (localStorage.getItem(key) === "1") continue;
        } catch { /* private mode — best effort */ }
        setThreshold(days);
        setOpen(true);
        return;
      }
    }
  }, [user, trialStatus]);

  const handleClose = () => {
    // Persist the LS flag only when the user actually saw & dismissed
    // the modal — StrictMode-safe and honest ("we asked, they said no").
    if (threshold != null) {
      try { localStorage.setItem(LS_PREFIX + threshold, "1"); } catch { /* ignore */ }
    }
    setOpen(false);
  };

  if (!open || !threshold) return null;

  const daysLeft = trialStatus?.days_left ?? threshold;
  const isCritical = threshold <= 3;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={handleClose}
      data-testid="trial-ending-modal"
    >
      <div
        className="relative max-w-md w-full rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: "var(--bg-primary)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          data-testid="trial-ending-close"
          aria-label="Close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-accent)] transition-colors"
        >
          <X size={18} />
        </button>

        {/* Header stripe */}
        <div
          className="p-6 pb-5"
          style={{
            background: isCritical
              ? "linear-gradient(135deg, #B23A3A 0%, #E09E50 100%)"
              : "linear-gradient(135deg, var(--accent-primary) 0%, #2D5A40 100%)",
          }}
        >
          <div className="flex items-center gap-2 text-white/80 text-xs font-semibold tracking-[0.14em] uppercase mb-2">
            <Zap size={13} />
            {isCritical
              ? t("trial_ending.header_critical", { defaultValue: "Your trial ends soon" })
              : t("trial_ending.header", { defaultValue: "Trial ending" })}
          </div>
          <h2 className="text-2xl font-heading font-medium text-white leading-tight" data-testid="trial-ending-title">
            {daysLeft <= 0
              ? t("trial_ending.title_today", { defaultValue: "Your trial ends today" })
              : daysLeft === 1
                ? t("trial_ending.title_one_day", { defaultValue: "1 day left in your trial" })
                : t("trial_ending.title_days_left", { defaultValue: `${daysLeft} days left in your trial`, count: daysLeft })}
          </h2>
        </div>

        <div className="p-6">
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-5">
            {t("trial_ending.body", {
              defaultValue: "Keep every Pro feature you've been using — Reports, Optimizer, AI Advisor, Streak, Circles, Wrap-up, Share hub. One tap, cancel anytime.",
            })}
          </p>

          <div className="rounded-xl border border-[var(--border-default)] p-4 mb-5" data-testid="trial-ending-price">
            <div className="flex items-baseline gap-2 mb-3">
              <div className="text-3xl font-heading font-medium text-[var(--text-primary)]">
                OMR 1
              </div>
              <div className="text-sm text-[var(--text-secondary)]">/ month</div>
            </div>
            <div className="text-xs text-[var(--accent-primary)] font-medium mb-3">
              {t("trial_ending.yearly_hint", { defaultValue: "or OMR 10 / year — save 17%" })}
            </div>
            <ul className="space-y-1.5 text-sm text-[var(--text-secondary)]">
              {[
                t("trial_ending.perk_reports", { defaultValue: "Full Reports & Optimizer" }),
                t("trial_ending.perk_advisor", { defaultValue: "AI Advisor with unlimited chats" }),
                t("trial_ending.perk_streak", { defaultValue: "Streak, Circles & Wrap-up" }),
                t("trial_ending.perk_cancel", { defaultValue: "Cancel anytime, no lock-in" }),
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <Check size={14} className="text-[var(--accent-primary)] mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => { handleClose(); navigate("/pricing"); }}
              data-testid="trial-ending-upgrade"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-white shadow-sm active:scale-95 transition-transform"
              style={{ background: "linear-gradient(135deg, var(--accent-primary), #2D5A40)" }}
            >
              <Zap size={15} />
              {t("trial_ending.cta_upgrade", { defaultValue: "Upgrade to Pro" })}
            </button>
            <button
              onClick={handleClose}
              data-testid="trial-ending-later"
              className="px-4 py-3 rounded-lg text-sm font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              {t("trial_ending.cta_later", { defaultValue: "Not now" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
